import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * Jeu d'icônes de la maquette. Le design system Unipros expose bien un composant `Icon`,
 * mais ses bundles sont des composants React : seuls les noms de glyphes sont réutilisables,
 * pas le code. Les tracés ci-dessous reprennent donc les mêmes glyphes (style Lucide,
 * 24×24, contour) que ceux référencés par la maquette.
 */
export type IconName =
  | 'layout-dashboard'
  | 'notebook'
  | 'briefcase-business'
  | 'circle-dot-dashed'
  | 'badge-euro'
  | 'bell'
  | 'network'
  | 'chevrons-left'
  | 'chevron-right'
  | 'chevron-down'
  | 'search'
  | 'download'
  | 'circle-plus'
  | 'triangle-alert'
  | 'arrow-right'
  | 'calendar-1'
  | 'info'
  | 'circle-alert'
  | 'check'
  | 'x'
  | 'ellipsis'
  | 'sun'
  | 'moon'
  | 'user'
  | 'image-plus'
  | 'trash-2'
  | 'star'
  | 'monitor'
  | 'package'
  | 'funnel';

const PATHS: Record<IconName, readonly string[]> = {
  'layout-dashboard': [
    'M3 3h7v9H3z',
    'M14 3h7v5h-7z',
    'M14 12h7v9h-7z',
    'M3 16h7v5H3z',
  ],
  notebook: ['M2 6h4', 'M2 10h4', 'M2 14h4', 'M2 18h4', 'M4 2h14a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H4z', 'M16 2v20'],
  'briefcase-business': [
    'M12 12h.01',
    'M16 6V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2',
    'M22 13a18.15 18.15 0 0 1-20 0',
    'M4 6h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z',
  ],
  'circle-dot-dashed': [
    'M10.1 2.18a10 10 0 0 1 3.8 0',
    'M17.6 3.71a10 10 0 0 1 2.69 2.7',
    'M21.82 10.1a10 10 0 0 1 0 3.8',
    'M20.29 17.6a10 10 0 0 1-2.7 2.69',
    'M13.9 21.82a10 10 0 0 1-3.8 0',
    'M6.4 20.29a10 10 0 0 1-2.69-2.7',
    'M2.18 13.9a10 10 0 0 1 0-3.8',
    'M3.71 6.4a10 10 0 0 1 2.7-2.69',
    'M12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4z',
  ],
  'badge-euro': [
    'M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z',
    'M7 12h5',
    'M15 9.4a4 4 0 1 0 0 5.2',
  ],
  bell: [
    'M10.268 21a2 2 0 0 0 3.464 0',
    'M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326',
  ],
  network: [
    'M17 16h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1z',
    'M3 16h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1z',
    'M10 2h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z',
    'M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3',
    'M12 12V8',
  ],
  'chevrons-left': ['m11 17-5-5 5-5', 'm18 17-5-5 5-5'],
  'chevron-right': ['m9 18 6-6-6-6'],
  'chevron-down': ['m6 9 6 6 6-6'],
  search: ['M11 3a8 8 0 1 0 0 16 8 8 0 0 0 0-16z', 'm21 21-4.3-4.3'],
  download: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'm7 10 5 5 5-5', 'M12 15V3'],
  'circle-plus': ['M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z', 'M8 12h8', 'M12 8v8'],
  'triangle-alert': [
    'm21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3',
    'M12 9v4',
    'M12 17h.01',
  ],
  'arrow-right': ['M5 12h14', 'm12 5 7 7-7 7'],
  'calendar-1': ['M11 14h1v4', 'M16 2v4', 'M3 10h18', 'M8 2v4', 'M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z'],
  info: ['M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z', 'M12 16v-4', 'M12 8h.01'],
  'circle-alert': ['M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z', 'M12 8v4', 'M12 16h.01'],
  check: ['M20 6 9 17l-5-5'],
  x: ['M18 6 6 18', 'm6 6 12 12'],
  ellipsis: ['M12 11a1 1 0 1 0 0 2 1 1 0 0 0 0-2z', 'M19 11a1 1 0 1 0 0 2 1 1 0 0 0 0-2z', 'M5 11a1 1 0 1 0 0 2 1 1 0 0 0 0-2z'],
  sun: [
    'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z',
    'M12 2v2',
    'M12 20v2',
    'm4.93 4.93 1.41 1.41',
    'm17.66 17.66 1.41 1.41',
    'M2 12h2',
    'M20 12h2',
    'm6.34 17.66-1.41 1.41',
    'm19.07 4.93-1.41 1.41',
  ],
  moon: ['M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z'],
  user: ['M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2', 'M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z'],
  'image-plus': [
    'M16 5h6',
    'M19 2v6',
    'M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5',
    'M9 7a2 2 0 1 0 0 4 2 2 0 0 0 0-4z',
    'M21 15l-3.086-3.086a2 2 0 0 0-2.828 0L6 21',
  ],
  'trash-2': [
    'M3 6h18',
    'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6',
    'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2',
    'M10 11v6',
    'M14 11v6',
  ],
  star: [
    'M12 2.5l2.95 5.98 6.6.96-4.77 4.65 1.12 6.57L12 17.56l-5.9 3.1 1.13-6.57L2.46 9.44l6.6-.96z',
  ],
  // Lucide « monitor » — le parc matériel. Distingue la liste du parc de « Mon matériel ».
  monitor: ['M4 3h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z', 'M8 21h8', 'M12 16v5'],
  // Lucide « package » — les packs de dotation, un lot d'équipements prêt à remettre.
  package: [
    'M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z',
    'M3.3 7 12 12l8.7-5',
    'M12 22V12',
  ],
  // Lucide « funnel » — filtrer une liste/datatable.
  funnel: [
    'M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z',
  ],
};

@Component({
  selector: 'uniit-icon',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg
      [attr.width]="size()"
      [attr.height]="size()"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      [attr.stroke-width]="strokeWidth()"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      @for (d of paths(); track d) {
        <path [attr.d]="d" />
      }
    </svg>
  `,
  styles: `
    :host {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
  `,
})
export class IconComponent {
  readonly name = input.required<IconName>();
  readonly size = input(18);
  readonly strokeWidth = input(1.8);

  protected readonly paths = computed(() => PATHS[this.name()] ?? []);
}
